import { useRef, useState, useCallback } from 'react';
import { Camera, ImagePlus, Trash2, Loader2, RefreshCw, X, Link } from 'lucide-react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { cn } from '@paulownia/shared/lib/utils';
import { getProductImageUrl, getProductThumbnailUrl } from '@paulownia/shared';
import { useImageUpload } from '../hooks/useImageUpload';
import { ImageCropper } from './ImageCropper';

interface ImageUploaderProps {
  tenantId: string;
  productId: string;
  currentImageUrl?: string;
  currentThumbnailUrl?: string;
  currentPictureUrl?: string; // Legacy URL field
  onImageUploaded?: (imageUrl: string, thumbnailUrl: string) => void;
  onImageDeleted?: () => void;
  onPictureUrlChange?: (url: string | undefined) => void;
  disabled?: boolean;
}

export function ImageUploader({
  tenantId,
  productId,
  currentImageUrl,
  currentThumbnailUrl,
  currentPictureUrl,
  onImageUploaded,
  onImageDeleted,
  onPictureUrlChange,
  disabled = false,
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState(currentPictureUrl || '');

  const {
    state,
    selectFile,
    processCrop,
    upload,
    retry,
    deleteImage,
    cancelCrop,
    reset,
    isUploading,
    isProcessing,
    isCropping,
    hasError,
  } = useImageUpload(tenantId);

  // Get current display image
  const displayImage = getProductImageUrl({ imageUrl: currentImageUrl, picture: currentPictureUrl });
  const thumbnailImage = getProductThumbnailUrl({
    thumbnailUrl: currentThumbnailUrl,
    imageUrl: currentImageUrl,
    picture: currentPictureUrl,
  });

  const hasUploadedImage = Boolean(currentImageUrl);
  const hasAnyImage = Boolean(displayImage);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await selectFile(file);

    // Reset input so same file can be selected again
    e.target.value = '';
  }, [selectFile]);

  const handleCropComplete = useCallback(async (croppedBlob: Blob) => {
    const processedImages = await processCrop(croppedBlob);
    if (processedImages) {
      // Pass processed images directly to upload to avoid async state issues
      const result = await upload(productId, processedImages);
      if (result && onImageUploaded) {
        onImageUploaded(result.imageUrl, result.thumbnailUrl);
      }
    }
  }, [processCrop, upload, productId, onImageUploaded]);

  const handleDelete = useCallback(async () => {
    if (hasUploadedImage) {
      const success = await deleteImage(productId);
      if (success && onImageDeleted) {
        onImageDeleted();
      }
    } else if (currentPictureUrl && onPictureUrlChange) {
      // Just clear the URL
      onPictureUrlChange(undefined);
      setUrlInput('');
    }
  }, [hasUploadedImage, deleteImage, productId, onImageDeleted, currentPictureUrl, onPictureUrlChange]);

  const handleRetry = useCallback(async () => {
    const result = await retry(productId);
    if (result && onImageUploaded) {
      onImageUploaded(result.imageUrl, result.thumbnailUrl);
    }
  }, [retry, productId, onImageUploaded]);

  const handleUrlSubmit = useCallback(() => {
    if (urlInput.trim() && onPictureUrlChange) {
      onPictureUrlChange(urlInput.trim());
      setShowUrlInput(false);
    }
  }, [urlInput, onPictureUrlChange]);

  const handleUrlCancel = useCallback(() => {
    setUrlInput(currentPictureUrl || '');
    setShowUrlInput(false);
  }, [currentPictureUrl]);

  // Show loading state
  const isLoading = isUploading || isProcessing;

  return (
    <div className="space-y-3">
      <Label>Image du produit</Label>

      {/* Image Preview or Upload Area */}
      <div className="relative">
        {hasAnyImage && !isLoading ? (
          // Image preview
          <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-square max-w-[200px]">
            <img
              src={thumbnailImage || displayImage}
              alt="Apercu du produit"
              className="w-full h-full object-cover"
            />
            {/* Delete button overlay */}
            {!disabled && (
              <button
                type="button"
                onClick={handleDelete}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                title="Supprimer l'image"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            {/* Upload indicator for uploaded images */}
            {hasUploadedImage && (
              <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                Telechargee
              </div>
            )}
          </div>
        ) : isLoading ? (
          // Loading state
          <div className="rounded-lg bg-gray-100 aspect-square max-w-[200px] flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mb-2" />
            <span className="text-sm text-gray-500">
              {isProcessing ? 'Traitement...' : `Envoi ${Math.round(state.progress)}%`}
            </span>
            {isUploading && (
              <div className="w-24 h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${state.progress}%` }}
                />
              </div>
            )}
          </div>
        ) : hasError ? (
          // Error state
          <div className="rounded-lg bg-red-50 border border-red-200 aspect-square max-w-[200px] flex flex-col items-center justify-center p-4">
            <X className="h-8 w-8 text-red-400 mb-2" />
            <span className="text-sm text-red-600 text-center mb-3">{state.error}</span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRetry}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Reessayer
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={reset}
              >
                Annuler
              </Button>
            </div>
          </div>
        ) : (
          // Empty state - upload area
          <div className={cn(
            "rounded-lg border-2 border-dashed border-gray-300 bg-gray-50",
            "aspect-square max-w-[200px] flex flex-col items-center justify-center p-4",
            disabled && "opacity-50 cursor-not-allowed"
          )}>
            <ImagePlus className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-500 text-center">
              Ajoutez une photo
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!isLoading && !hasError && !disabled && (
        <div className="flex flex-wrap gap-2">
          {/* Camera button (mobile) */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            className="flex-1 min-w-[100px]"
          >
            <Camera className="h-4 w-4 mr-1" />
            Photo
          </Button>

          {/* Gallery button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 min-w-[100px]"
          >
            <ImagePlus className="h-4 w-4 mr-1" />
            Galerie
          </Button>

          {/* URL button */}
          {onPictureUrlChange && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowUrlInput(!showUrlInput)}
              className="px-2"
              title="Ajouter une URL"
            >
              <Link className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* URL Input */}
      {showUrlInput && onPictureUrlChange && (
        <div className="space-y-2 pt-2 border-t">
          <Label className="text-sm text-gray-600">URL de l'image</Label>
          <div className="flex gap-2">
            <Input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://..."
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              onClick={handleUrlSubmit}
              disabled={!urlInput.trim()}
            >
              OK
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleUrlCancel}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Crop modal */}
      {state.previewUrl && isCropping && (
        <ImageCropper
          imageSrc={state.previewUrl}
          isOpen={isCropping}
          onClose={cancelCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
        />
      )}
    </div>
  );
}
